<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import { IDERELETIVEHEIGHT } from '@/utils/ide'
import { IDEVIEWTABS, TAB_BUTTON_POSITION, TAB_ICONS_NAMES } from '@/utils/tabs'
import { useIdeStore } from '@/stores/ide.store'
import {
  useApiTabStore,
  type AuthorizationField,
  type BodyField,
  type ParamsField
} from '@/stores/apiTab.store'
import TabCompTitleBar from '@/components/ide/ide/TabCompTitleBar.vue'
import HTTPRequestPanel from '@/components/ide/ide/tabs/ApiTabComp/HTTPRequestPanel.vue'
import OutputResponse from '@/components/ide/ide/tabs/ApiTabComp/OutputResponse/OutputResponse.vue'
import {
  AUTHORIZATION_AUTH_TYPES,
  BODY_RAW_TYPES,
  BODY_TYPES,
  CORS_ERROR,
  DISALLOWEDDOMAINS,
  DISALLOWEDPATTERNS,
  HTTP_REQUEST_METHODS,
  JWT_AVAILABLE_ALGORITHMS,
  JWT_TOKEN_ADD_TO,
  RESPONSE_FIELD
} from '@/utils/apiTab'
import BaseSelect from '@/components/forms/BaseSelect.vue'
import { FORM_SELECT_VALUE_TYPE } from '@/components/forms/form.enum'
import { RequestSection } from '@/utils/apiTab'
import apiBrowserIcon from '@/assets/images/ide/apiBrowserIcon.svg'
import closeIcon from '@/assets/images/ide/closebutton.svg'
import addHttpRequestPlusIcon from '@/assets/images/ide/addRequestPlus.svg'

interface MethodInterface {
  method: HTTP_REQUEST_METHODS
  url: string
  id: number
  Params: ParamsField[]
  Authorization: AuthorizationField
  Headers: ParamsField[]
  Body: BodyField
}

const statusColorMap: any = {
  200: 'bg-green-600',
  400: 'bg-yellow-600',
  404: 'bg-red-600',
  500: 'bg-red-600'
}

const props = defineProps({
  tabPosition: {
    type: String,
    required: false,
    default: TAB_BUTTON_POSITION.BOTTOM
  }
})
const ideStore = useIdeStore()
const apiTabStore = useApiTabStore()

const httpRequestMethods = Object.values(HTTP_REQUEST_METHODS)
const responseField = Object.values(RESPONSE_FIELD)

const defaultHttpRequestMethod = ref<string>(HTTP_REQUEST_METHODS.GET)
const selectedRequestField = ref<RequestSection>(RequestSection.PARAMS)
const domainAddress = ref<string>('')
const apiResponse = ref<string>('')
const errorResponse = ref<string>('')
const apiResponseObject = ref<any>({})
const executeButtonDisabled = ref<boolean>(true)

const activeRequests = computed(() => {
  return apiTabStore.activeTabRequests
})

const activeRequestIndex = computed(() => {
  const activeRequest = apiTabStore.activeTabRequests.find((item) => item.active)
  return activeRequest ? activeRequest.id : 1
})

const activeUrl = computed(() => {
  const activeUrl =
    activeRequests.value && activeRequests.value.find((item) => item.id == activeRequestIndex.value)

  return activeUrl?.url ? activeUrl.url : 'Test request'
})

const appendedStatus = computed(() => {
  if (apiResponseObject.value?.status || apiResponseObject.value?.statusText) {
    return `${apiResponseObject.value?.status} ${apiResponseObject.value?.statusText}`
  } else {
    return undefined
  }
})

const statusColor = computed(() => {
  const status = apiResponseObject.value?.status
  return statusColorMap[status] || 'bg-gray-600'
})

const truncatedUrl = computed(() => {
  const maxLength = 40
  return activeUrl.value.length > maxLength
    ? activeUrl.value.substring(0, maxLength) + '...'
    : activeUrl.value
})

const containerWidth = computed(() => {
  return calculateWidth()
})

/**
 * Sets the selected request field to a valid `RequestSection` value if it is present.
 */
const setRequestField = () => {
  if (Object.values(RequestSection).includes(selectedRequestField.value as RequestSection)) {
    selectedRequestField.value = selectedRequestField.value as RequestSection
    apiTabStore.updateActiveRequestField(activeRequestIndex.value, selectedRequestField.value)
  }
}

/**
 * Sets the default HTTP request method if it is valid and updates the active request method in the API tab store.
 */
const setRequestMethod = () => {
  if (
    Object.values(HTTP_REQUEST_METHODS).includes(
      defaultHttpRequestMethod.value as HTTP_REQUEST_METHODS
    )
  ) {
    defaultHttpRequestMethod.value = defaultHttpRequestMethod.value as HTTP_REQUEST_METHODS
    apiTabStore.updateActiveRequestMethod(activeRequestIndex.value, defaultHttpRequestMethod.value)
  }
}
/**
 * Adds a new HTTP request by creating a new active tab in the API tab store.
 */
const addNewHttpRequest = () => {
  apiTabStore.setCreateActiveTabRequests()
}
/**
 * Sets the active HTTP request by its unique ID.
 * @param id - The unique identifier of the request to set as active.
 */
const setActiveRequest = (id: number) => {
  apiTabStore.setActiveRequest(id)
}
/**
 * Handles input events by updating the active request URL in the API tab store.
 * @param event - The input event triggered by the user.
 */
const handleInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  apiTabStore.updateActiveRequestUrl(activeRequestIndex.value, value)
}
/**
 * Handles focus event by setting flags in the `apiTabStore` to control update behaviors.
 * Calls `setCanUpdateParam` with `true` to enable parameter updates, and `setCanUpdateUrl` with `false` to disable URL updates.
 */
const handleFocus = () => {
  apiTabStore.setCanUpdateParam(true)
  apiTabStore.setCanUpdateUrl(false)
}
/**
 * Updates the initial URL and HTTP method for the active request.
 */
const updateInitialUrlAndMethod = () => {
  activeRequests.value &&
    activeRequests.value.forEach((item) => {
      if (item.id == activeRequestIndex.value) {
        domainAddress.value = item?.url || ''

        defaultHttpRequestMethod.value = item?.method || HTTP_REQUEST_METHODS.GET
        selectedRequestField.value =
          (item?.responseField as RequestSection) || RequestSection.PARAMS
      }
    })
  resetErrorLogs()
}
/**
 * Removes an HTTP request by its unique ID.
 * @param id - The unique identifier of the request to be removed.
 */
const removeHttpRequest = (id: number) => {
  apiTabStore.removeRequest(id)
}
/**
 * Get the content type based on the provided raw body type.
 * @param type - The raw body type, which can be one of the predefined BODY_RAW_TYPES.
 * @returns The corresponding content type as a string.
 */
const getContentType = (type: string) => {
  switch (type) {
    case BODY_RAW_TYPES.TEXT:
      return 'text/plain'
    case BODY_RAW_TYPES.JAVASCRIPT:
      return 'application/javascript'
    case BODY_RAW_TYPES.JSON:
      return 'application/json'
    case BODY_RAW_TYPES.HTML:
      return 'text/html'
    case BODY_RAW_TYPES.XML:
      return 'application/xml'
    default:
      return 'application/json'
  }
}

/**
 * Resets the error logs by clearing the values of apiResponse, errorResponse, and apiResponseObject.
 */
const resetErrorLogs = () => {
  apiResponse.value = ''
  errorResponse.value = ''
  apiResponseObject.value = {}
}

/**
 * Determines if the request method is not GET.
 * @param  method - The HTTP method for the request (GET, POST, etc.).
 * @returns - True if the method is not GET, otherwise false.
 */
const isMethodNotGet = (method: string): boolean => {
  return method.toUpperCase() !== 'GET'
}

/**
 * Converts an ArrayBuffer to a Base64URL encoded string.
 * @param  buffer - The buffer to be converted.
 * @returns  The Base64URL encoded string representation of the buffer.
 */
const arrayBufferToBase64Url = (buffer: any) => {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_')
}

/**
 * Base64URL encodes the provided data.
 * @param data - The data to be encoded.
 * @returns  The Base64URL encoded string.
 */
const base64UrlEncode = (data: any) =>
  btoa(JSON.stringify(data)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_')

/**
 * Creates a JSON Web Token (JWT) using the specified algorithm.
 * @param  headerData - The JWT header, containing algorithm and type.
 * @param  payloadData - The JWT payload, containing user claims.
 * @param  secret - The secret key used for signing the JWT.
 * @param  algorithm - The algorithm used for signing the JWT (e.g., HS256, HS384, HS512).
 * @returns  The generated JWT.
 */
const createJWT = async (headerData: any, payloadData: any, secret: any, algorithm = 'HS256') => {
  const encoder = new TextEncoder()

  const hashName =
    algorithm === JWT_AVAILABLE_ALGORITHMS.HS256
      ? 'SHA-256'
      : algorithm === JWT_AVAILABLE_ALGORITHMS.HS384
      ? 'SHA-384'
      : algorithm === JWT_AVAILABLE_ALGORITHMS.HS512
      ? 'SHA-512'
      : 'SHA-256'

  const encodedHeader = base64UrlEncode(headerData)
  const encodedPayload = base64UrlEncode(payloadData)

  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: { name: hashName } },
    false,
    ['sign']
  )

  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    encoder.encode(`${encodedHeader}.${encodedPayload}`)
  )

  return `${encodedHeader}.${encodedPayload}.${arrayBufferToBase64Url(signature)}`
}

/**
 * Builds a headers object from a list of header key-value pairs.
 * @param  headersList - An array of header objects, each containing a `key` and `value`.
 * @param  Authorization - An object containing authorization details.
 * @param  method - The HTTP method being used.
 * @param  Body - The request body to determine Content-Type.
 * @returns - A headers object where each non-empty key-value pair is included.
 */
const buildHeaders = async (
  headersList: ParamsField[],
  Authorization: AuthorizationField,
  method: HTTP_REQUEST_METHODS,
  Body: BodyField
): Promise<{ headers: { [key: string]: string }; jwt?: string }> => {
  const headers = headersList.reduce((acc: { [key: string]: string }, header) => {
    if (header.key && header.value && header.active) {
      acc[header.key] = header.value
    }
    return acc
  }, {})

  let jwt

  if (isMethodNotGet(method)) {
    if (Body?.raw?.rawValue) {
      headers['Content-Type'] = getContentType(Body.raw.rawContentType)
    } else if (Body?.xWwwFormUrlencoded && Body?.xWwwFormUrlencoded.length > 0) {
      headers['Content-Type'] = 'application/x-www-form-urlencoded'
    }
  }

  if (Authorization) {
    if (
      (Authorization.basicAuth?.userName || Authorization.basicAuth?.passWord) &&
      Authorization.selected === AUTHORIZATION_AUTH_TYPES.BASIC_AUTH
    ) {
      const { userName, passWord } = Authorization.basicAuth
      const base64Credentials = btoa(`${userName}:${passWord}`)
      headers['Authorization'] = `Basic ${base64Credentials}`
    } else if (
      Authorization.bearerToken?.token &&
      Authorization.selected == AUTHORIZATION_AUTH_TYPES.BEARER_TOKEN
    ) {
      headers['Authorization'] = `Bearer ${Authorization.bearerToken.token}`
    } else if (
      Authorization.jwtBearer &&
      Authorization.selected == AUTHORIZATION_AUTH_TYPES.JWT_BEARER
    ) {
      const { algorithm, payload, secret, tokenAddToField } = Authorization.jwtBearer
      if (algorithm && payload && secret) {
        const header = { alg: algorithm, typ: 'JWT' }
        const jwt = await createJWT(header, payload, secret, header.alg)

        if (tokenAddToField === JWT_TOKEN_ADD_TO.REQUEST_HEADERS) {
          headers['Authorization'] = `Bearer ${jwt}`
          return { headers }
        } else {
          return { headers, jwt }
        }
      }
    }
  }

  return { headers, jwt }
}

/**
 * Builds the request body for non-GET HTTP methods.
 * @param  method - The HTTP method (e.g., GET, POST, PUT).
 * @param Body - The body object containing either raw data or formData.
 * @returns - Returns the constructed request body (raw string, URLSearchParams for form data, or null if no body is needed).
 */
const buildRequestBody = (method: string, Body: BodyField) => {
  let body: any = null

  if (isMethodNotGet(method)) {
    if (Body?.formData && Body.formData.length > 0 && Body.selected == BODY_TYPES.FORM_DATA) {
      body = new FormData()
      Body.formData.forEach((item) => {
        if (item.key && item.value && item.active) {
          if (item.selectedFile) {
            body.append(item.key, item.selectedFile, item.value)
          } else {
            body.append(item.key, item.value)
          }
        }
      })
    } else if (
      Body?.xWwwFormUrlencoded &&
      Body.xWwwFormUrlencoded.length > 0 &&
      Body.selected == BODY_TYPES.FORM_URLENCODED
    ) {
      body = new URLSearchParams()
      Body.xWwwFormUrlencoded.forEach((item) => {
        if (item.key && item.value) {
          body.append(item.key, item.value)
        }
      })
    } else if (Body?.raw?.rawValue && Body.selected == BODY_TYPES.RAW_DATA) {
      body = Body.raw.rawValue
    }
  }

  return body
}

/**
 * Executes an HTTP request based on the provided method interface data.
 * @param data - The request details including method, URL, parameters, headers, and body.
 * @returns The JSON response from the server.
 */
const executeRequest = async (data: MethodInterface) => {
  const { method, url, Authorization, Headers, Body } = data

  let finalUrl = url
  const { headers, jwt } = await buildHeaders(Headers, Authorization, method, Body)
  const body = buildRequestBody(method, Body)

  if (Authorization?.jwtBearer?.tokenAddToField === JWT_TOKEN_ADD_TO.QUERY_PARAM && jwt) {
    finalUrl = finalUrl.replace(/\?$/, '')
    const separator = finalUrl.includes('?') ? '&' : '?'
    finalUrl += `${separator}token=${encodeURIComponent(jwt)}`
  }

  let options = {}
  if (
    method.toUpperCase() === HTTP_REQUEST_METHODS.GET ||
    method.toUpperCase() === HTTP_REQUEST_METHODS.HEAD
  ) {
    options = {
      method: method.toUpperCase(),
      headers: headers
    }
  } else {
    options = {
      method: method.toUpperCase(),
      headers: headers,
      body: body
    }
  }

  return await fetch(finalUrl, options)
    .then(async (response) => {
      if (!response.ok) {
        try {
          const errorText = await response.text()
          errorResponse.value = errorText
          apiResponseObject.value = response
          return null
        } catch (error) {
          errorResponse.value = error as string
        }
      }
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        const result = await response.json().catch(() => {})
        apiResponse.value = JSON.stringify(result)
        apiResponseObject.value = response
        return result
      } else {
        const textResponse = await response.text()
        apiResponse.value = textResponse
        return textResponse
      }
    })
    .catch((err) => {
      if (!errorResponse.value) {
        try {
          if (typeof err === 'object') {
            const strErr = err.toString()
            if (strErr.includes('TypeError')) {
              errorResponse.value = CORS_ERROR
            } else {
              errorResponse.value = err
            }
          }
        } catch (error) {
          errorResponse.value = err
        }
      }
    })
}

/**
 * Sends an HTTP request using the data from the active request index.
 * @returns A promise that resolves when the request is sent.
 */
const sendRequest = async () => {
  resetErrorLogs()
  if (executeButtonDisabled.value) {
    return
  }
  const data = apiTabStore.getAllRequiredData(activeRequestIndex.value)
  executeRequest(data as MethodInterface)
}
/**
 * Determines if a domain is allowed based on disallowed domains and patterns.
 * @param domainAddress - The domain address to check.
 * @returns - Returns `true` if allowed, `false` if disallowed.
 */
const isDomainAllowed = (domainAddress: string) => {
  if (DISALLOWEDDOMAINS.includes(domainAddress)) {
    return false
  }
  for (const pattern of DISALLOWEDPATTERNS) {
    if (pattern.test(domainAddress)) {
      return false
    }
  }
  return true
}
/**
 * Calculates the width based on whether the current tab is mobile or not.
 *
 * - If no mobile tab is active (`ideStore.currentMobileTab` is falsy), it returns `'w-full'`, indicating full width.
 * - If a mobile tab is active, it attempts to get the width of an element by its ID (`IDERELETIVEHEIGHT.MINIMUMID`) and logs the width to the console.
 * @returns  Returns 'w-full' if no mobile tab is active, otherwise returns nothing.
 */
const calculateWidth = () => {
  if (!ideStore.currentMobileTab) {
    return '100%'
  } else {
    const el = document.getElementById(IDERELETIVEHEIGHT.MINIMUMID)
    const width = el?.clientWidth || 0 // width of setting sidebar for mobile
    return `calc(100vw - ${width}px)`
  }
}

onMounted(() => {
  if (activeRequests.value.length === 0) {
    addNewHttpRequest()
  }
  updateInitialUrlAndMethod()
})

watch(
  () => activeRequestIndex.value,
  () => {
    updateInitialUrlAndMethod()
  }
)
/**
 * Parses a URL and extracts query parameters into an array of ParamsField objects.
 * Each ParamsField object contains the parameter's key, value, a default empty description, and is marked as active.
 * @param url - The URL containing query parameters to parse.
 * @returns An array of ParamsField objects with each query parameter from the URL.
 */
const parseUrlToParams = (url: string): ParamsField[] => {
  const paramsArray: ParamsField[] = []
  const queryStart = url.indexOf('?')

  if (queryStart === -1) return paramsArray

  const queryString = url.substring(queryStart + 1)
  const params = new URLSearchParams(queryString)

  for (const [key, value] of params.entries()) {
    paramsArray.push({
      key,
      value,
      description: '',
      active: true
    })
  }

  return paramsArray
}
/**
 * Converts an array of active query parameters to a URL with query string.
 * @param  baseUrl - The base URL without query parameters.
 * @param  params - An array of parameters to be included in the query string.
 * Only parameters with `active: true` and non-empty `key` will be included.
 * @returns  - The constructed URL with active query parameters appended.
 */
const paramsToUrl = (baseUrl: string, params: ParamsField[]): string => {
  if (!Array.isArray(params)) {
    return baseUrl
  }
  const activeParams = params && params?.filter((param) => param.active && param.key)
  const searchParams = new URLSearchParams()

  activeParams.forEach((param) => {
    searchParams.append(param.key, param.value)
  })

  const urlPrefix = baseUrl ? baseUrl.split('?')[0] : ''

  return activeParams.length > 0 ? `${urlPrefix}?${searchParams.toString()}` : urlPrefix
}

watch(
  () => domainAddress.value,
  () => {
    if (apiTabStore.canUpdateParam) {
      let finalRowValue = parseUrlToParams(domainAddress.value)
      apiTabStore.updateRequestField(activeRequestIndex.value, RequestSection.PARAMS, finalRowValue)

      const isAllowedDomain = isDomainAllowed(domainAddress.value)
      const isValidProtocol =
        domainAddress.value.startsWith('https://') || domainAddress.value.startsWith('http://')

      if (!isAllowedDomain || !domainAddress.value || !isValidProtocol) {
        executeButtonDisabled.value = true
      } else {
        executeButtonDisabled.value = false
      }
    }
  }
)

watch(
  () => apiTabStore.requestFields.map((request) => request.Params),
  () => {
    if (apiTabStore.canUpdateUrl) {
      const paramsData = apiTabStore.getRequestFieldData(
        activeRequestIndex.value,
        RequestSection.PARAMS
      )

      let finalUrl = paramsToUrl(domainAddress.value, paramsData as ParamsField[])
      if (finalUrl) {
        domainAddress.value = finalUrl
        apiTabStore.updateActiveRequestUrl(activeRequestIndex.value, finalUrl)
      } else {
        domainAddress.value = ''
      }
    }
  },
  { deep: true }
)
</script>

<template>
  <div
    class="surface-primary-base relative flex h-full w-full flex-col p-0"
    :id="`${IDEVIEWTABS.API_TAB}-main`"
  >
    <TabCompTitleBar
      v-if="ideStore.currentMobileTab == null && props.tabPosition == TAB_BUTTON_POSITION.MAXIMIZE"
      titleName="API tester"
      :icon="apiBrowserIcon"
      :tabPosition="props.tabPosition"
      :tabName="TAB_ICONS_NAMES.API_TAB"
      :customClass="true"
    />

    <div
      class="background-primary flex h-full w-full flex-col gap-4 overflow-hidden rounded p-2"
      :class="{ 'px-4': props.tabPosition === TAB_BUTTON_POSITION.BOTTOM }"
      :style="{ width: containerWidth }"
    >
      <div class="flex min-h-8 min-w-[100%] max-w-[0px] flex-row gap-2 overflow-x-auto py-1.5">
        <div class="flex h-full" v-for="item in activeRequests" :key="item.id">
          <button
            class="flex flex-row items-center justify-center gap-2"
            @click="setActiveRequest(item.id)"
          >
            <div
              class="text-high flex min-h-6 max-w-32 items-center overflow-hidden text-ellipsis whitespace-nowrap text-xs font-normal"
              :class="{ 'stroke-secondary-default border-b-2': item.active }"
            >
              {{ item.method ? item.method : HTTP_REQUEST_METHODS.GET }}
              {{ item.url ? item.url : 'Test request' }}
            </div>

            <div v-if="activeRequests.length > 1">
              <button type="button" class="h-2.5 w-2.5" @click.stop="removeHttpRequest(item.id)">
                <img :src="closeIcon" />
              </button>
            </div>
          </button>
        </div>
        <div class="flex items-center">
          <button type="button" class="h-4 w-4" @click="addNewHttpRequest">
            <img :src="addHttpRequestPlusIcon" />
          </button>
        </div>
      </div>
      <div class="text-mid flex min-h-7">
        {{ truncatedUrl }}
      </div>
      <div class="flex h-full w-full flex-col overflow-y-auto">
        <div class="flex h-10 w-full flex-row items-center rounded border border-border-primary">
          <div class="flex h-full">
            <BaseSelect
              :list="httpRequestMethods"
              :valueType="FORM_SELECT_VALUE_TYPE.NAME"
              v-model="defaultHttpRequestMethod"
              customClass="!min-w-[90px] appearance-none background-primary border-none rounded-md text-high tracking-tight outline-none"
              @change="setRequestMethod"
              :showLable="false"
              :getSelectedByItem="true"
              :isLightGray="false"
            />
          </div>
          <div class="flex w-full flex-row items-center justify-center">
            <div class="w-full p-1">
              <input
                type="text"
                v-model="domainAddress"
                @input="handleInput"
                @focus="handleFocus"
                placeholder="Enter URL or paste text"
                class="w-full min-w-0 truncate bg-inherit p-1 outline-none"
              />
            </div>
            <div className="h-9 justify-start items-start inline-flex p-0.5">
              <div
                className=" self-stretch px-4 py-3.5   bg-new-secondary-600 rounded-tr rounded-br justify-center items-center gap-2.5 flex"
              >
                <button
                  :disabled="executeButtonDisabled"
                  @click="sendRequest"
                  :class="{
                    'cursor-not-allowed opacity-50': executeButtonDisabled,
                    'text-center text-xs font-semibold tracking-tight text-white': true
                  }"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-4 flex h-10 w-full">
          <BaseSelect
            :list="responseField"
            :valueType="FORM_SELECT_VALUE_TYPE.NAME"
            v-model="selectedRequestField"
            customClass="background-primary text-high block h-9 w-full appearance-none rounded-md tracking-tight !px-0.5 !pr-8  outline-none border-none"
            @change="setRequestField"
            :getSelectedByItem="true"
            :showLable="false"
            :isLightGray="false"
          />
        </div>
        <div class="mt-4 flex">
          <HTTPRequestPanel
            :selectedRequestFieldValue="selectedRequestField"
            :activeRequestIndex="activeRequestIndex"
          />
        </div>
        <div class="background-primary m-auto mt-8 flex w-full flex-col gap-4 px-0.5 pb-10">
          <div class="flex flex-row gap-5">
            <div class="text-high flex items-center text-xs font-normal">Response</div>
            <div
              v-if="appendedStatus"
              class="text-high rounded border p-1 text-xs font-medium"
              :class="statusColor"
            >
              {{ appendedStatus }}
            </div>
          </div>
          <OutputResponse
            :apiResponse="apiResponse"
            :errorResponse="errorResponse"
            :tabPosition="props.tabPosition"
          />
        </div>
      </div>
    </div>
  </div>
</template>
